import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';

import { environment } from '../../../../environments/environment';
import { KiwixiService } from '../kiwixi/kiwixi.service';
import { KiwixiGlobals } from '../kiwixi/kiwixi.globals';
import { MaterialService } from './material.service';
import {
  IdentityVerification,
  IdentityVerificationSessionResponse,
  IdentityPhotosResponse,
  IdentityCapture,
} from '../model/identity-verification.model';

/**
 * Service de gestion de la vérification d'identité via Stripe Identity
 * Source: api-recurring/recurring/exam_identity/documentation/how_to.md
 */
@Injectable()
export class IdentityVerificationService extends KiwixiService {
  private readonly BASE_URL = environment.server;

  constructor(
    private _http: HttpClient,
    private _router: Router,
    kiwixiGlobals: KiwixiGlobals,
    private materialService: MaterialService,
  ) {
    super(_http, _router, kiwixiGlobals, materialService);
  }

  /**
   * Démarre une vérification d'identité Stripe (scopé au test LILATE)
   * POST /api/v1/lilate_exams/{pk}/identity/start-verification/
   * Body optionnel: { "return_url": "..." }
   * Réponse 201: { client_secret, url, session_id }
   */
  startLilateVerification(lilateId: number, returnUrl?: string): Observable<IdentityVerificationSessionResponse> {
    const headers = this.createHttpClientAuthorizationHeaderNoEnctype();
    const body = returnUrl ? { return_url: returnUrl } : {};
    return this._http.post<IdentityVerificationSessionResponse>(
      `${this.BASE_URL}lilate_exams/${lilateId}/identity/start-verification/`,
      body,
      { headers }
    ).pipe(
      catchError(this.handleErrorObservable<IdentityVerificationSessionResponse>('startLilateVerification'))
    );
  }

  /**
   * Démarre une vérification d'identité Stripe (scopé au test ELATE)
   * POST /api/v1/elates/{pk}/identity/start-verification/
   */
  startElateVerification(elateId: number, returnUrl?: string): Observable<IdentityVerificationSessionResponse> {
    const headers = this.createHttpClientAuthorizationHeaderNoEnctype();
    const body = returnUrl ? { return_url: returnUrl } : {};
    return this._http.post<IdentityVerificationSessionResponse>(
      `${this.BASE_URL}elates/${elateId}/identity/start-verification/`,
      body,
      { headers }
    ).pipe(
      catchError(this.handleErrorObservable<IdentityVerificationSessionResponse>('startElateVerification'))
    );
  }

  /**
   * Marque la session courante comme expirée (scopé au test LILATE).
   * Appelé quand Stripe retourne { error: { code: 'session_does_not_exist' } }.
   * POST /api/v1/lilate_exams/{pk}/identity/expire-session/
   */
  expireLilateSession(lilateId: number): Observable<{ status: string; attempts_count: number }> {
    const headers = this.createHttpClientAuthorizationHeaderNoEnctype();
    return this._http.post<{ status: string; attempts_count: number }>(
      `${this.BASE_URL}lilate_exams/${lilateId}/identity/expire-session/`,
      {},
      { headers }
    ).pipe(
      catchError(this.handleErrorObservable<{ status: string; attempts_count: number }>('expireLilateSession'))
    );
  }

  /**
   * Récupère le statut complet d'une vérification d'identité (scopé au test LILATE)
   * GET /api/v1/lilate_exams/{pk}/identity/status/
   * Réponse 200: { id, status, attempts_count, verified_at, failure_reason, reviewed_by, reviewed_at, review_notes }
   * Si aucune vérification: { "status": "pending", "attempts_count": 0 }
   */
  getLilateVerificationStatus(lilateId: number): Observable<IdentityVerification> {
    const headers = this.createHttpClientAuthorizationHeader();
    return this._http.get<IdentityVerification>(
      `${this.BASE_URL}lilate_exams/${lilateId}/identity/status/`,
      { headers }
    ).pipe(
      catchError(this.handleErrorObservable<IdentityVerification>('getLilateVerificationStatus'))
    );
  }

  /**
   * Marque la session courante comme expirée (scopé au test ELATE).
   * POST /api/v1/elates/{pk}/identity/expire-session/
   */
  expireElateSession(elateId: number): Observable<{ status: string; attempts_count: number }> {
    const headers = this.createHttpClientAuthorizationHeaderNoEnctype();
    return this._http.post<{ status: string; attempts_count: number }>(
      `${this.BASE_URL}elates/${elateId}/identity/expire-session/`,
      {},
      { headers }
    ).pipe(
      catchError(this.handleErrorObservable<{ status: string; attempts_count: number }>('expireElateSession'))
    );
  }

  /**
   * Récupère le statut complet d'une vérification d'identité (scopé au test ELATE)
   * GET /api/v1/elates/{pk}/identity/status/
   */
  getElateVerificationStatus(elateId: number): Observable<IdentityVerification> {
    const headers = this.createHttpClientAuthorizationHeader();
    return this._http.get<IdentityVerification>(
      `${this.BASE_URL}elates/${elateId}/identity/status/`,
      { headers }
    ).pipe(
      catchError(this.handleErrorObservable<IdentityVerification>('getElateVerificationStatus'))
    );
  }

  /**
   * Upload une capture caméra pendant le test (scopé au test LILATE)
   * POST /api/v1/lilate_exams/{pk}/identity/capture/
   * Multipart: file ou capture, capture_type (ignoré côté back), optionnel capture_request_id
   * Réponse 201: { id, capture_type, captured_at }
   */
  uploadLilateCapture(lilateId: number, file: File, captureRequestId?: string): Observable<any> {
    const headers = this.createHttpClientAuthorizationHeader();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('capture_type', 'camera_capture'); // Ignoré côté back, toujours camera_capture
    if (captureRequestId) {
      formData.append('capture_request_id', captureRequestId);
    }

    return this._http.post(
      `${this.BASE_URL}lilate_exams/${lilateId}/identity/capture/`,
      formData,
      { headers }
    ).pipe(
      catchError(this.handleErrorObservable<any>('uploadLilateCapture'))
    );
  }

  /**
   * Upload une capture caméra pendant le test (scopé au test ELATE)
   * POST /api/v1/elates/{pk}/identity/capture/
   */
  uploadElateCapture(elateId: number, file: File, captureRequestId?: string): Observable<any> {
    const headers = this.createHttpClientAuthorizationHeader();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('capture_type', 'camera_capture');
    if (captureRequestId) {
      formData.append('capture_request_id', captureRequestId);
    }

    return this._http.post(
      `${this.BASE_URL}elates/${elateId}/identity/capture/`,
      formData,
      { headers }
    ).pipe(
      catchError(this.handleErrorObservable<any>('uploadElateCapture'))
    );
  }

  /**
   * Staff override pour débloquer un test en échec définitif (scopé au test LILATE)
   * POST /api/v1/lilate_exams/{pk}/identity-verification/staff-override/
   * Body: { "review_notes": "..." }
   * Conditions (côté back) : échec définitif, session bloquée (≥ 2 tentatives), ou absence d'enregistrement IV.
   */
  staffOverrideLilate(lilateId: number, reviewNotes: string): Observable<IdentityVerification> {
    const headers = this.createHttpClientAuthorizationHeaderNoEnctype();
    return this._http.post<IdentityVerification>(
      `${this.BASE_URL}lilate_exams/${lilateId}/identity-verification/staff-override/`,
      { review_notes: reviewNotes },
      { headers }
    ).pipe(
      catchError(this.handleErrorObservable<IdentityVerification>('staffOverrideLilate'))
    );
  }

  /**
   * Staff override pour débloquer un test en échec définitif (scopé au test ELATE)
   * POST /api/v1/elates/{pk}/identity-verification/staff-override/
   */
  staffOverrideElate(elateId: number, reviewNotes: string): Observable<IdentityVerification> {
    const headers = this.createHttpClientAuthorizationHeaderNoEnctype();
    return this._http.post<IdentityVerification>(
      `${this.BASE_URL}elates/${elateId}/identity-verification/staff-override/`,
      { review_notes: reviewNotes },
      { headers }
    ).pipe(
      catchError(this.handleErrorObservable<IdentityVerification>('staffOverrideElate'))
    );
  }

  /**
   * Récupère les URLs signées éphémères (~30min) des photos Stripe (document + selfie).
   * Staff uniquement. GET /api/v1/lilate_exams/{pk}/identity/photos/
   * Réponse: { available, doc_front_url, doc_back_url, selfie_url }
   */
  getLilateIdentityPhotos(lilateId: number): Observable<IdentityPhotosResponse> {
    const headers = this.createHttpClientAuthorizationHeader();
    return this._http.get<IdentityPhotosResponse>(
      `${this.BASE_URL}lilate_exams/${lilateId}/identity/photos/`,
      { headers }
    ).pipe(
      catchError(this.handleErrorObservable<IdentityPhotosResponse>('getLilateIdentityPhotos'))
    );
  }

  /**
   * Liste des vérifications en échec définitif nécessitant une revue staff
   * GET /api/v1/identity-verifications/pending-staff-review/
   * Filtres par défaut: status='definitively_failed', tri -created_at, limite 50
   */
  getPendingStaffReview(): Observable<IdentityVerification[]> {
    const headers = this.createHttpClientAuthorizationHeader();
    return this._http.get<IdentityVerification[]>(
      `${this.BASE_URL}identity-verifications/pending-staff-review/`,
      { headers }
    ).pipe(
      catchError(this.handleErrorObservable<IdentityVerification[]>('getPendingStaffReview'))
    );
  }

  /**
   * Liste les captures caméra d'un test LILATE (staff uniquement)
   * GET /api/v1/lilate_exams/{pk}/identity/captures/
   * Réponse: [{id, captured_at, capture_request_id, mime_type, file_size, data_url}]
   */
  getLilateCaptures(lilateId: number): Observable<IdentityCapture[]> {
    const headers = this.createHttpClientAuthorizationHeader();
    return this._http.get<IdentityCapture[]>(
      `${this.BASE_URL}lilate_exams/${lilateId}/identity/captures/`,
      { headers }
    ).pipe(
      catchError(this.handleErrorObservable<IdentityCapture[]>('getLilateCaptures'))
    );
  }

  /**
   * Récupère les URLs signées éphémères (~30s) des photos Stripe (document + selfie).
   * Staff uniquement. GET /api/v1/elates/{pk}/identity/photos/
   */
  getElateIdentityPhotos(elateId: number): Observable<IdentityPhotosResponse> {
    const headers = this.createHttpClientAuthorizationHeader();
    return this._http.get<IdentityPhotosResponse>(
      `${this.BASE_URL}elates/${elateId}/identity/photos/`,
      { headers }
    ).pipe(
      catchError(this.handleErrorObservable<IdentityPhotosResponse>('getElateIdentityPhotos'))
    );
  }

  /**
   * Liste les captures caméra d'un test ELATE (staff uniquement)
   * GET /api/v1/elates/{pk}/identity/captures/
   */
  getElateCaptures(elateId: number): Observable<IdentityCapture[]> {
    const headers = this.createHttpClientAuthorizationHeader();
    return this._http.get<IdentityCapture[]>(
      `${this.BASE_URL}elates/${elateId}/identity/captures/`,
      { headers }
    ).pipe(
      catchError(this.handleErrorObservable<IdentityCapture[]>('getElateCaptures'))
    );
  }

  // ─── Endpoints externes FlashLevel (par token, sans auth JWT) ──────────────

  /**
   * Démarre une vérification d'identité Stripe via l'API externe FlashLevel
   * POST /api/external/v1/flashlevel/{token}/identity/start-verification/
   */
  startFlashlevelVerificationExternal(token: string, returnUrl?: string): Observable<IdentityVerificationSessionResponse> {
    const body = returnUrl ? { return_url: returnUrl } : {};
    return this._http.post<IdentityVerificationSessionResponse>(
      `${environment.base_server}/api/external/v1/flashlevel/${token}/identity/start-verification/`,
      body
    ).pipe(
      catchError(this.handleErrorObservable<IdentityVerificationSessionResponse>('startFlashlevelVerificationExternal'))
    );
  }

  /**
   * Récupère le statut de vérification via l'API externe FlashLevel
   * GET /api/external/v1/flashlevel/{token}/identity/status/
   */
  getFlashlevelVerificationStatusExternal(token: string): Observable<IdentityVerification> {
    return this._http.get<IdentityVerification>(
      `${environment.base_server}/api/external/v1/flashlevel/${token}/identity/status/`
    ).pipe(
      catchError(this.handleErrorObservable<IdentityVerification>('getFlashlevelVerificationStatusExternal'))
    );
  }

  /**
   * Marque la session courante comme expirée via l'API externe FlashLevel
   * POST /api/external/v1/flashlevel/{token}/identity/expire-session/
   */
  expireFlashlevelSessionExternal(token: string): Observable<{ status: string; attempts_count: number }> {
    return this._http.post<{ status: string; attempts_count: number }>(
      `${environment.base_server}/api/external/v1/flashlevel/${token}/identity/expire-session/`,
      {}
    ).pipe(
      catchError(this.handleErrorObservable<{ status: string; attempts_count: number }>('expireFlashlevelSessionExternal'))
    );
  }
}
